import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '@/components/SEO';
import { createContact } from '@/lib/api';
import { toast } from 'sonner';
import { 
  CheckCircle, ArrowRight, Loader2, Play, 
  Target, BarChart3, Zap, TrendingUp, Star,
  Phone, Mail, Clock
} from 'lucide-react';
import CountdownTimer from '@/components/CountdownTimer';
import ExitIntentPopup from '@/components/ExitIntentPopup';
import { trackFormSubmit } from '@/lib/gtm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c422f3aa-3782-49d4-8e3c-f15c34e2c74c/artifacts/halfbrs0_wizbang.png";

const benefits = [
  "Complete Website Management & Development",
  "Social Media Management & Strategy",
  "Performance Marketing & Ads",
  "SEO & Digital Analytics",
  "Dedicated Account Manager",
  "Monthly Performance Reports",
];

const stats = [
  { value: "500%", label: "Average ROI" },
  { value: "150+", label: "Happy Clients" },
  { value: "10M+", label: "Revenue Generated" },
];

const testimonials = [
  {
    quote: "Wizbang transformed our digital presence. Our leads increased by 300% in just 3 months.",
    author: "Marketing Director",
    company: "Tech Startup",
    rating: 5,
  },
  {
    quote: "Finally, a team that understands revenue, not just marketing metrics. Highly recommended!",
    author: "Founder",
    company: "E-commerce Brand",
    rating: 5,
  },
];

const painPoints = [
  "Tired of marketing that doesn't convert?",
  "Wasting money on ads with no ROI?",
  "Need a partner, not just another agency?",
];

export default function LandingPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    budget: '',
  });

  // Track UTM parameters
  useEffect(() => {
    const utmParams = {
      utm_source: searchParams.get('utm_source') || '',
      utm_medium: searchParams.get('utm_medium') || '',
      utm_campaign: searchParams.get('utm_campaign') || '',
      utm_content: searchParams.get('utm_content') || '',
      utm_term: searchParams.get('utm_term') || '',
    };
    
    // Store in session for tracking
    sessionStorage.setItem('wizbang_utm', JSON.stringify(utmParams));
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const utmData = JSON.parse(sessionStorage.getItem('wizbang_utm') || '{}');
    
    try {
      await createContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: `[LEAD] ${formData.company || 'New Lead'} - Budget: ${formData.budget}`,
        message: `Company: ${formData.company}\nBudget: ${formData.budget}\n\nUTM Source: ${utmData.utm_source}\nUTM Medium: ${utmData.utm_medium}\nUTM Campaign: ${utmData.utm_campaign}`,
      });
      const filled = Object.values(formData).filter(Boolean).length;
      trackFormSubmit('lead_magnet_form', filled);
      setSubmitted(true);
      toast.success('Thank you! We\'ll contact you within 24 hours.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="landing-success">
        <div className="max-w-lg text-center">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-syne text-4xl font-bold text-white mb-4">
            You're In!
          </h1>
          <p className="text-[#888] text-lg mb-8">
            One of our growth experts will reach out within 24 hours to discuss your free strategy session.
          </p>
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6 text-left">
            <p className="text-[#555] text-sm mb-4">While you wait, here's what to expect:</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-[#ff5a1f] flex-shrink-0 mt-0.5" />
                <span className="text-white">30-minute strategy call with a growth expert</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-[#ff5a1f] flex-shrink-0 mt-0.5" />
                <span className="text-white">Free audit of your current marketing</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-[#ff5a1f] flex-shrink-0 mt-0.5" />
                <span className="text-white">Custom growth roadmap for your business</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="landing-page">
      <SEO 
        title="Get a Free Growth Strategy Session"
        description="Claim your free 30-minute growth strategy session. Get a custom marketing roadmap, free audit, and actionable insights to 10x your business."
        keywords="free marketing strategy, growth consultation, digital marketing audit, free marketing session"
        url="/grow"
        noIndex={true}
      />
      
      {/* Exit Intent Popup */}
      <ExitIntentPopup />

      {/* Urgency Bar */}
      <div className="bg-[#ff5a1f] py-3">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <div className="flex items-center gap-2 text-white text-sm font-medium">
            <Clock className="w-4 h-4" />
            <span>Offer Expires In:</span>
          </div>
          <CountdownTimer />
          <span className="text-white/80 text-sm">— Only 5 Spots Left Today!</span>
        </div>
      </div>

      {/* Minimal Header */}
      <header className="py-6 border-b border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <img src={LOGO_URL} alt="Wizbang" className="h-8" />
          <a
            href="tel:9878780298"
            className="flex items-center gap-2 text-white text-sm hover:text-[#ff5a1f] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">9878780298</span>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial opacity-40" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 bg-[#ff5a1f]/10 border border-[#ff5a1f]/20 px-4 py-2 mb-8">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#ff5a1f] fill-[#ff5a1f]" />
                  ))}
                </div>
                <span className="text-[#ff5a1f] text-sm font-medium">Rated 5/5 by 150+ Clients</span>
              </div>

              {/* Pain Points */}
              <div className="space-y-2 mb-6">
                {painPoints.map((point, idx) => (
                  <p key={idx} className="text-[#888] text-lg line-through decoration-[#ff5a1f]/50">
                    {point}
                  </p>
                ))}
              </div>

              {/* Headline */}
              <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Get a <span className="text-[#ff5a1f]">Free Growth</span> Strategy Worth ₹25,000
              </h1>

              <p className="text-[#888] text-lg mb-8 leading-relaxed">
                Stop guessing. Start growing. Book your free 30-minute strategy session and discover how to <strong className="text-white">5x your revenue</strong> with our proven growth systems.
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {benefits.slice(0, 4).map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#ff5a1f] flex-shrink-0" />
                    <span className="text-white text-sm">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="flex gap-8 pt-8 border-t border-[#1F1F1F]">
                {stats.map((stat, idx) => (
                  <div key={idx}>
                    <p className="font-syne text-3xl font-bold text-[#ff5a1f]">{stat.value}</p>
                    <p className="text-[#888] text-xs uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Lead Form */}
            <div className="lg:pl-8">
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-8 relative">
                {/* Form Header */}
                <div className="absolute -top-4 left-8 bg-[#ff5a1f] text-white text-xs font-bold uppercase tracking-wider px-4 py-2">
                  Limited Spots Available
                </div>

                <h2 className="font-syne text-2xl font-bold text-white mb-2 mt-4">
                  Claim Your Free Strategy Session
                </h2>
                <p className="text-[#888] text-sm mb-6">
                  No obligation. No credit card required.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Name *"
                      data-testid="lead-name-input"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors placeholder:text-[#555]"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Work Email *"
                      data-testid="lead-email-input"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors placeholder:text-[#555]"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Phone Number *"
                      data-testid="lead-phone-input"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors placeholder:text-[#555]"
                    />
                  </div>

                  <div>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors placeholder:text-[#555]"
                    />
                  </div>

                  <div>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      required
                      data-testid="lead-budget-select"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                    >
                      <option value="">Monthly Marketing Budget *</option>
                      <option value="Under ₹50,000">Under ₹50,000</option>
                      <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
                      <option value="₹1,00,000 - ₹3,00,000">₹1,00,000 - ₹3,00,000</option>
                      <option value="₹3,00,000 - ₹5,00,000">₹3,00,000 - ₹5,00,000</option>
                      <option value="Above ₹5,00,000">Above ₹5,00,000</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-submit-btn"
                    className="w-full bg-[#ff5a1f] text-white px-8 py-5 text-sm font-bold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Get My Free Strategy
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-[#555] text-xs text-center">
                    🔒 Your information is 100% secure and will never be shared.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 bg-[#0A0A0A]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#ff5a1f] font-mono text-sm uppercase tracking-widest mb-4">
              // What's Included
            </p>
            <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">
              Your Free Strategy Session Includes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: "Marketing Audit",
                desc: "Complete analysis of your current marketing efforts and opportunities for growth.",
              },
              {
                icon: BarChart3,
                title: "Competitor Analysis",
                desc: "See what your competitors are doing and how to outperform them.",
              },
              {
                icon: TrendingUp,
                title: "Custom Roadmap",
                desc: "A detailed 90-day action plan tailored to your business goals.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="p-8 bg-[#050505] border border-[#1F1F1F] hover:border-[#ff5a1f]/50 transition-all text-center"
              >
                <div className="w-16 h-16 bg-[#ff5a1f]/10 flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#ff5a1f]" />
                </div>
                <h3 className="font-syne text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-[#888] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#ff5a1f] font-mono text-sm uppercase tracking-widest mb-4">
              // Success Stories
            </p>
            <h2 className="font-syne text-3xl md:text-4xl font-bold text-white">
              Trusted by Growth-Focused Brands
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="p-8 bg-[#0A0A0A] border border-[#1F1F1F]"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#ff5a1f] fill-[#ff5a1f]" />
                  ))}
                </div>
                <p className="text-white text-lg mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="text-white font-medium">{testimonial.author}</p>
                  <p className="text-[#888] text-sm">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#0A0A0A] relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-30" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-syne text-3xl md:text-4xl font-bold text-white mb-6">
            Stop Losing Money on
            <br />
            <span className="text-[#ff5a1f]">Marketing That Doesn't Work</span>
          </h2>
          <p className="text-[#888] text-lg mb-8">
            Join 150+ businesses that have transformed their growth with Wizbang.
          </p>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="bg-[#ff5a1f] text-white px-10 py-5 text-sm font-bold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors inline-flex items-center gap-2"
          >
            Claim Your Free Strategy Now
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-[#555] text-sm mt-6">
            Only 5 spots available this week
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#1F1F1F]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={LOGO_URL} alt="Wizbang" className="h-6" />
            <div className="flex items-center gap-6 text-sm text-[#888]">
              <a href="mailto:sayhi@wizbang.in" className="flex items-center gap-2 hover:text-[#ff5a1f] transition-colors">
                <Mail className="w-4 h-4" />
                sayhi@wizbang.in
              </a>
              <a href="tel:9878780298" className="flex items-center gap-2 hover:text-[#ff5a1f] transition-colors">
                <Phone className="w-4 h-4" />
                9878780298
              </a>
            </div>
            <p className="text-[#555] text-xs">© 2026 Wizbang. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
